/**
 * MagicLoginPage — MAGIC-1 (2026-07-05).
 *
 * Landing page for the emailed sign-in link (/magic-login?token=...).
 * Exchanges the one-time token for a session via AuthContext, then routes
 * by role exactly like LoginPage. Tokens are single-use + 15-minute expiry,
 * so failures land on a friendly "request a new link" screen.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicToken } = useAuth();
  const [error, setError] = useState("");
  // React 18 StrictMode double-invokes effects in dev; the token is
  // single-use so the second invocation would burn a "already used" error.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    const token = searchParams.get("token") || "";
    if (!token) {
      setError("This sign-in link is missing its token. Please request a new one.");
      return;
    }
    (async () => {
      try {
        const user = await loginWithMagicToken(token);
        if (user.must_change_password) {
          navigate("/change-password");
        } else if (user.role === "introducer") {
          navigate("/introducer");
        } else if (user.role === "partner") {
          navigate("/partner/dashboard");
        } else {
          navigate("/portal");
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "This sign-in link is invalid, expired, or already used.");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full max-w-md p-8 text-center" data-testid="magic-login-page">
        {!error ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-[#bd9a59] mx-auto" />
            <h1 className="text-lg font-semibold text-[#021d49] mt-4">Signing you in…</h1>
            <p className="text-sm text-gray-500 mt-1">One moment while we verify your link.</p>
          </>
        ) : (
          <>
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
            <h1 className="text-lg font-semibold text-[#021d49] mt-4">Link didn't work</h1>
            <p className="text-sm text-gray-600 mt-2" data-testid="magic-login-error">{error}</p>
            <Link
              to="/login"
              className="inline-block mt-5 px-4 py-2 text-sm bg-[#021d49] text-white rounded hover:bg-[#052b66]"
              data-testid="magic-login-back"
            >
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
